import React, { Component, ErrorInfo, ReactNode } from "react";
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import { Button } from "@/components/ui/button";
import { logError, formatErrorReport, getErrorLog } from "@/utils/errorReporting";
import { BUILD_HASH, APP_VERSION } from "@/config/version";
import { Copy, RefreshCw, Home, Bug, ChevronDown } from "lucide-react";

interface Props {
  children: ReactNode;
  fallback?: ReactNode;
}

interface State {
  hasError: boolean;
  error: Error | null;
  errorId: string | null;
  copied: boolean;
  showDetails: boolean;
}

export class ErrorBoundary extends Component<Props, State> {
  public state: State = {
    hasError: false,
    error: null,
    errorId: null,
    copied: false,
    showDetails: false,
  };

  public static getDerivedStateFromError(error: Error): Partial<State> {
    return { hasError: true, error };
  }

  public componentDidCatch(error: Error, errorInfo: ErrorInfo) {
    // Log error with full details
    const report = logError(error, errorInfo.componentStack || undefined);
    this.setState({ errorId: report.id });
  }

  private handleReset = () => {
    this.setState({ hasError: false, error: null, errorId: null });
    window.location.href = "/play";
  };

  private handleRefresh = () => {
    window.location.reload();
  };

  private handleCopyError = async () => {
    const log = getErrorLog();
    const latest = log[0];
    
    if (latest) {
      const report = formatErrorReport(latest);
      try {
        await navigator.clipboard.writeText(report);
        this.setState({ copied: true });
        setTimeout(() => this.setState({ copied: false }), 2000);
      } catch {
        // Fallback for browsers without clipboard API
        console.log(report);
      }
    }
  };

  public render() {
    if (this.state.hasError) {
      if (this.props.fallback) {
        return this.props.fallback;
      }

      return (
        <div className="min-h-screen flex items-center justify-center p-4 bg-background">
          <div className="max-w-lg w-full space-y-4">
            <Alert variant="destructive">
              <Bug className="h-5 w-5" />
              <AlertTitle className="text-lg font-semibold">
                Something went wrong
              </AlertTitle>
              <AlertDescription className="space-y-4 mt-3">
                <p className="text-sm">
                  We hit an unexpected error. This has been logged automatically.
                  Try refreshing or returning to the game.
                </p>

                {this.state.errorId && (
                  <p className="text-xs font-mono bg-muted/50 px-2 py-1 rounded inline-block">
                    Error ID: {this.state.errorId}
                  </p>
                )}

                <div className="flex flex-wrap gap-2">
                  <Button onClick={this.handleRefresh} variant="default" size="sm" className="gap-2">
                    <RefreshCw className="h-4 w-4" />
                    Refresh
                  </Button>
                  <Button onClick={this.handleReset} variant="outline" size="sm" className="gap-2">
                    <Home className="h-4 w-4" />
                    Return Home
                  </Button>
                  <Button 
                    onClick={this.handleCopyError} 
                    variant="outline" 
                    size="sm" 
                    className="gap-2"
                  >
                    <Copy className="h-4 w-4" />
                    {this.state.copied ? "Copied!" : "Copy Error"}
                  </Button>
                </div>
              </AlertDescription>
            </Alert>

            {/* Error details (collapsible) */}
            <button
              onClick={() => this.setState(s => ({ showDetails: !s.showDetails }))}
              className="w-full text-left text-xs text-muted-foreground flex items-center gap-1 hover:text-foreground transition-colors"
            >
              <ChevronDown className={`h-3 w-3 transition-transform ${this.state.showDetails ? 'rotate-180' : ''}`} />
              Technical Details
            </button>

            {this.state.showDetails && this.state.error && (
              <div className="text-xs bg-muted p-3 rounded space-y-2 overflow-auto max-h-48">
                <div>
                  <span className="font-semibold">Build:</span>{" "}
                  <span className="font-mono">v{APP_VERSION}+{BUILD_HASH.slice(0, 7)}</span>
                </div>
                <div>
                  <span className="font-semibold">Error:</span>{" "}
                  <span className="font-mono">{this.state.error.message}</span>
                </div>
                {this.state.error.stack && (
                  <details>
                    <summary className="cursor-pointer font-semibold">Stack Trace</summary>
                    <pre className="whitespace-pre-wrap mt-1 text-[10px] leading-tight">
                      {this.state.error.stack}
                    </pre>
                  </details>
                )}
              </div>
            )}

            <p className="text-xs text-muted-foreground text-center">
              If this keeps happening, try clearing your cache in Settings.
            </p>
          </div>
        </div>
      );
    }

    return this.props.children;
  }
}
